import { GenericObjectType, UiSchema } from "@rjsf/utils";
import type { JSONSchema7Definition } from "json-schema";
import { useMemo } from "react";
import { useIntl } from "react-intl";

import { objectEntries } from "@nens/shared-utils";

import { typeHereString } from "@/translations";

import { getRanaWidgetFromProperty } from "..";
import { ProcessRunFormProps } from "../process-run-form";

export function useProcessRunForm<T extends GenericObjectType>({
  schema,
  isOutput,
}: ProcessRunFormProps<T>) {
  const intl = useIntl();

  const uiSchema = useMemo(() => {
    const getUiSchema = (schema: JSONSchema7Definition) => {
      if (typeof schema === "boolean") return;
      const ranaWidget = getRanaWidgetFromProperty(schema);

      const baseUiOptions = {
        title: "",
      };
      const uiSchema: UiSchema = {
        "ui:placeholder": typeHereString(intl),
        "ui:options": baseUiOptions,
        isOutput,
        "ui:field": ranaWidget?.id,
        "ui:fieldReplacesAnyOrOneOf":
          schema.anyOf?.length || schema.oneOf?.length ? true : undefined,
        "ui:title": "",
      };

      // If there are any ui: properties, add them to the uiSchema
      objectEntries(schema).forEach(([key, value]) => {
        if (key.startsWith("ui:")) uiSchema[key] = value;
      });

      if (schema.properties) {
        objectEntries(schema.properties).forEach(([key, property]) => {
          uiSchema[key] = getUiSchema(property);
        });
      }

      if (schema.anyOf) {
        uiSchema.anyOf = schema.anyOf.map(getUiSchema);
      }

      if (schema.oneOf) {
        uiSchema.oneOf = schema.oneOf.map(getUiSchema);
      }

      if (schema.items && typeof schema.items !== "boolean") {
        uiSchema.items = Array.isArray(schema.items)
          ? schema.items.map(getUiSchema)
          : getUiSchema(schema.items);
      }

      return uiSchema;
    };

    if (!schema?.properties) return;

    const uiSchema = getUiSchema(schema);
    return uiSchema;
  }, [intl, isOutput, schema]);

  return { uiSchema };
}
